import { Vec3 } from '../math/vec3';
import { AABB } from '../math/aabb';

export type TriggerShape = { kind: 'box'; aabb: AABB } | { kind: 'sphere'; center: Vec3; r: number };

export interface Trigger {
  id: string;
  shape: TriggerShape;
  enabled: boolean;
  /** Arbitrary payload for the game. */
  data?: unknown;
  onEnter?: (who: number, t: Trigger) => void;
  onExit?: (who: number, t: Trigger) => void;
  onStay?: (who: number, t: Trigger, dt: number) => void;
}

/** Volumes with enter/exit/stay events per tracked entity. */
export class TriggerSystem {
  readonly triggers: Trigger[] = [];
  private inside = new Map<number, Set<Trigger>>();

  add(t: Omit<Trigger, 'enabled'> & { enabled?: boolean }): Trigger {
    const tr: Trigger = { enabled: true, ...t };
    this.triggers.push(tr);
    return tr;
  }
  remove(t: Trigger): void {
    const i = this.triggers.indexOf(t);
    if (i >= 0) this.triggers.splice(i, 1);
    for (const s of this.inside.values()) s.delete(t);
  }
  box(id: string, aabb: AABB, opts: Partial<Trigger> = {}): Trigger { return this.add({ id, shape: { kind: 'box', aabb }, ...opts }); }
  sphere(id: string, center: Vec3, r: number, opts: Partial<Trigger> = {}): Trigger { return this.add({ id, shape: { kind: 'sphere', center, r }, ...opts }); }

  static contains(t: Trigger, p: Vec3): boolean {
    if (t.shape.kind === 'box') return t.shape.aabb.containsPoint(p);
    return t.shape.center.distanceToSq(p) <= t.shape.r * t.shape.r;
  }

  /** Evaluate all triggers for one entity at position p. */
  update(who: number, p: Vec3, dt: number): void {
    let set = this.inside.get(who);
    if (!set) this.inside.set(who, (set = new Set()));
    for (const t of this.triggers) {
      const now = t.enabled && TriggerSystem.contains(t, p);
      const was = set.has(t);
      if (now && !was) { set.add(t); t.onEnter?.(who, t); }
      else if (!now && was) { set.delete(t); t.onExit?.(who, t); }
      if (now) t.onStay?.(who, t, dt);
    }
  }
  isInside(who: number, t: Trigger): boolean { return this.inside.get(who)?.has(t) ?? false; }
  /** Triggers the entity is currently inside, optionally filtered by id prefix. */
  currentFor(who: number, prefix?: string): Trigger[] {
    const s = this.inside.get(who);
    if (!s) return [];
    return [...s].filter((t) => !prefix || t.id.startsWith(prefix));
  }
  forget(who: number): void { this.inside.delete(who); }
  clear(): void { this.triggers.length = 0; this.inside.clear(); }
}
